import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { StatusBar } from '@capacitor/status-bar';
import { initAppTheme } from '@/lib/app-theme';
import { reloadOnceForNewVersion } from '@/lib/chunk-reload';
import { initViewportFit } from '@/lib/viewport-fit';
import { initKeyboardViewportFix } from '@/lib/app-shell';
import { captureOAuthReturnError } from '@/lib/oauth-return-error';
import { adoptSiteVisitor, markChallengeArrival } from '@/lib/analytics/track';

// Écran trop étroit (zoom du téléphone) : page de 375 px réduite pour tenir,
// cf. lib/viewport-fit.ts. Avant le premier rendu.
initViewportFit();
// iOS : page restée décalée après la fermeture du clavier (barre du bas au milieu).
initKeyboardViewportFix();

// Mode clair / sombre (préférence de l'appareil, cf. lib/app-theme.ts).
initAppTheme();

// Page ouverte avant un déploiement : un fichier chargé à la demande n'existe plus → on
// recharge une fois (cf. lib/chunk-reload.ts). Événement émis par Vite.
window.addEventListener('vite:preloadError', (e) => {
  if (reloadOnceForNewVersion()) e.preventDefault();
});

// Retour Google / Facebook / Apple en échec : on garde le message pour la page de
// connexion (sinon l'utilisateur revient sans rien voir), cf. lib/oauth-return-error.ts.
captureOAuthReturnError();

// Visiteur venu du site (`?jv=`) : même visiteur dans les tunnels de conversion.
adoptSiteVisitor();
// Arrivée depuis un défi partagé du site (`?from=challenge`) : tunnel « défis ».
markChallengeArrival();

if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {
    /* SW optionnel */
  });
}

// Tout ce bloc est gated par isNativePlatform() : zéro effet sur le web.
if (Capacitor.isNativePlatform()) {
  // Marque <html> pour cibler les zones système (barre de statut, notch) via
  // le variant Tailwind `native:` (styles/globals.css).
  document.documentElement.classList.add('jr-native');

  // Retour du deep link OAuth (Google/Facebook/Apple) — cf. lib/native-oauth.ts
  // et handleOAuth dans otp-auth-form.tsx. Écouteur global : l'app peut recevoir
  // ce lien même si l'écran d'origine a été démonté entre-temps.
  // Import dynamique de @capacitor/browser : ce code n'est atteint que sur
  // natif, donc le web (Vite dev/build) n'a jamais besoin que ce package soit
  // installé — évite de casser l'app web si `pnpm install` n'a pas encore
  // tourné après l'ajout d'une dépendance native (cf. CAPACITOR_MOBILE_PLAN.md).
  //
  // Le code PKCE est à usage unique : on mémorise les URLs déjà traitées pour
  // ne pas rejouer l'échange quand `getLaunchUrl` et `appUrlOpen` livrent tous
  // les deux le même lien (démarrage à froid).
  const handledOAuthUrls = new Set<string>();

  const handleOAuthDeepLink = async (url: string) => {
    const callback = parseNativeOAuthCallback(url);
    if (!callback || handledOAuthUrls.has(url)) return;
    handledOAuthUrls.add(url);

    const { Browser } = await import('@capacitor/browser');
    await Browser.close().catch(() => {
      /* déjà fermé */
    });

    if (callback.kind === 'error') {
      console.error('[oauth] provider a refusé la connexion', callback.message);
      return;
    }
    const { error } = await supabase.auth.exchangeCodeForSession(callback.code);
    if (error) console.error('[oauth] exchangeCodeForSession failed', error);
  };

  void CapacitorApp.addListener('appUrlOpen', ({ url }) => void handleOAuthDeepLink(url));

  // Démarrage à froid : si l'OS a tué l'app pendant que l'utilisateur était sur
  // la page du provider, le deep link relance l'app et `appUrlOpen` peut avoir
  // déjà été émis avant que ce listener soit posé. Le code verifier PKCE, lui,
  // survit (localStorage) — l'échange reste possible.
  void CapacitorApp.getLaunchUrl().then((launch) => {
    if (launch?.url) void handleOAuthDeepLink(launch.url);
  });

  // Tap sur une notification push (FCM natif) → deep link vers `data.url` (ex.
  // /learn/exercise/<id> côté élève, /tutor/students/<id> côté prof). Le web,
  // lui, gère ce cas dans public/sw.js (notificationclick). Écouteur global :
  // fonctionne quel que soit l'écran monté, comme le deep link OAuth ci-dessus.
  void FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    const path = resolveNotificationPath(event?.notification?.data);
    if (path) void router.navigate({ to: path });
  });

  // Notification reçue app OUVERTE : on recharge le Feed. C'est le « temps
  // réel » de l'app — le serveur envoie déjà un push pour chaque nouveauté du
  // fil, autant s'en servir comme signal plutôt que d'ouvrir un abonnement
  // Realtime par table (devoirs, défis, discussions, SKILLCARDS…).
  void FirebaseMessaging.addListener('notificationReceived', () => {
    invalidateFeed();
  });

  // Enregistrement automatique du téléphone pour les notifications push à
  // chaque session (cf. lib/notifications/native-push-sync.ts) ; token
  // supprimé à la déconnexion. Hors du callback (setTimeout) : supabase-js
  // déconseille d'appeler ses API depuis onAuthStateChange (verrou interne).
  // Léger délai pour que l'écran soit affiché avant la demande de permission.
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT') {
      setTimeout(() => void detachNativePush(), 0);
    } else if (session && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
      setTimeout(() => void syncNativePush(session), 1500);
    }
  });

  // Rotation du jeton FCM (réinstallation, restauration, expiration) : écoute
  // GLOBALE. Avant, elle ne vivait que sur « Mon profil » — un jeton révoqué
  // ailleurs n'était jamais remplacé côté serveur et l'appareil ne recevait
  // plus rien.
  watchNativePushToken(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  });

  // Retour de l'app au premier plan : on revérifie le jeton. Firebase peut
  // l'avoir révoqué pendant que l'app dormait ; c'est le seul moment où on
  // peut s'en apercevoir sans attendre la prochaine connexion.
  void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) return;
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) void syncNativePush(data.session, { force: true });
    });
  });

  // Bouton retour matériel Android — remonte l'historique du router (TanStack
  // Router s'appuie sur l'historique navigateur par défaut) plutôt que de
  // fermer l'app immédiatement.
  void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      void CapacitorApp.exitApp();
    }
  });

  // Style de la barre de statut : posé par initAppTheme() selon le mode
  // clair/sombre. `overlaysWebView` : le contenu peut passer sous la barre de
  // statut, compensé par le variant `native:` (safe-area).
  void StatusBar.setOverlaysWebView({ overlay: true });
}

import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from '@/lib/auth';
import { queryClient } from '@/lib/query-client';
import { router } from '@/router';
import { supabase } from '@/lib/supabase';
import { parseNativeOAuthCallback } from '@/lib/native-oauth';
import { FirebaseMessaging } from '@/lib/notifications/firebase-messaging';
import { invalidateFeed } from '@/lib/news/feed-refresh';
import { resolveNotificationPath } from '@/lib/notifications/deep-link';
import {
  detachNativePush,
  syncNativePush,
  watchNativePushToken,
} from '@/lib/notifications/native-push-sync';
import { AuthTransitionPopup } from '@/features/auth-transition/auth-transition-popup';
import { notePathChange } from '@/lib/exercise-exit';
import '@/styles/globals.css';
import 'katex/dist/katex.min.css';

// Page précédente (SPA) : sortie d'un exercice au retour d'un paiement Stripe.
router.subscribe('onResolved', (e) => notePathChange(e.fromLocation?.href, e.toLocation.href));

function InnerApp() {
  const auth = useAuth();

  // Masque le splash natif une fois le 1er contenu React monté et peint —
  // évite le flash de contenu non stylé entre le splash et l'app (le splash
  // reste affiché jusque-là grâce à SplashScreen.launchAutoHide=false,
  // cf. capacitor.config.ts). Ne fait rien sur le web.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) void SplashScreen.hide();
  }, []);

  // Init RevenueCat dès qu'on a l'uuid de l'user (natif seulement). L'uuid
  // Supabase sert d'`appUserID` RC → c'est cette clé qui remonte dans les
  // webhooks vers l'edge fn `revenuecat-webhook`. Import dynamique pour ne
  // pas charger le SDK sur web.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const userId = auth.session?.user?.id;
    if (!userId) return;
    void import('@/lib/checkout').then(({ initRevenueCat }) => initRevenueCat(userId));
  }, [auth.session?.user?.id]);

  // Ré-évalue les guards après un changement de session (OTP verify, OAuth,
  // logout). On ne déclenche PAS l'invalidation pendant loading=true : à ce
  // stade RouterProvider n'est pas encore monté et le router utiliserait son
  // contexte initial (session: null), ce qui provoquerait une redirection
  // prématurée vers /login.
  useEffect(() => {
    if (auth.loading) return;
    router.invalidate();
  }, [auth.session, auth.loading, auth.profile]);

  // Bloque le router jusqu'à ce que l'auth initiale soit résolue.
  // Ainsi, beforeLoad reçoit toujours un état stable (loading=false)
  // et peut rediriger immédiatement sans logique réactive.
  if (auth.loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <span className="text-ink-400 text-sm">Chargement…</span>
      </div>
    );
  }

  return (
    <>
      <RouterProvider router={router} context={{ auth, queryClient }} />
      {/* Popup anime de bienvenue / au revoir — frere du router pour survivre a
          la redirection vers /login que la deconnexion declenche. */}
      <AuthTransitionPopup />
    </>
  );
}

const rootEl = document.getElementById('root')!;
type RootedElement = typeof rootEl & { _reactRoot?: ReturnType<typeof ReactDOM.createRoot> };
const rootedEl = rootEl as RootedElement;
if (!rootedEl._reactRoot) {
  rootedEl._reactRoot = ReactDOM.createRoot(rootEl);
}
rootedEl._reactRoot.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <InnerApp />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
